import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

// Reusable Google + Apple sign-in block used on both LoginPage and
// SignupPage. We render whichever providers have a client-ID env var
// configured at build time. If neither is set, the whole block
// disappears so non-prod builds don't show dead buttons.
//
// Google: loads accounts.google.com/gsi/client lazily and renders the
// official Google-styled button. The `prompt_parent_id` lets Google
// know where to anchor the One Tap prompt if it triggers.
//
// Apple: loads appleid.cdn-apple.com/appleauth/static/jsapi/.../appleid.auth.js
// and uses AppleID.auth.init / AppleID.auth.signIn(). Returns the same
// id_token shape the backend expects.

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID  = process.env.REACT_APP_APPLE_CLIENT_ID  || '';
// Apple's redirect URI must exactly match what's registered in the
// Apple Developer portal. We default to the current origin's /login
// page but allow an env override.
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== 'undefined' ? `${window.location.origin}/login` : '');

const loadScriptOnce = (src) => {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(s);
  });
};

const SocialAuthButtons = ({ onSuccess, onError, mode = 'login' }) => {
  const { loginWithGoogle, loginWithApple } = useAuth();
  const { toast } = useToast();
  const googleBtnRef = useRef(null);
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleAvailable, setAppleAvailable] = useState(Boolean(APPLE_CLIENT_ID));

  // ----- Google ----------------------------------------------------------
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return;

    let cancelled = false;
    loadScriptOnce('https://accounts.google.com/gsi/client')
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            try {
              const result = await loginWithGoogle(response.credential);
              if (result?.requires2FA || result?.requires2FASetup) {
                // Hand the 2FA case back to the parent page so it can
                // open the existing 2FA modal.
                onSuccess?.(result);
                return;
              }
              toast({
                title: mode === 'signup' ? 'Welcome to Ariv!' : 'Welcome back!',
                description: `Signed in as ${result.user.name}`,
              });
              onSuccess?.(result);
            } catch (err) {
              const detail = err.response?.data?.detail || 'Google sign-in failed';
              toast({ title: 'Sign-in failed', description: detail, variant: 'destructive' });
              onError?.(err);
            }
          },
          // Stop Google from auto-selecting an account silently — better
          // UX to always show the chooser first time.
          auto_select: false,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          width: 320,
          shape: 'rectangular',
        });
      })
      .catch((e) => {
        console.warn('Google sign-in script failed to load:', e);
      });

    return () => { cancelled = true; };
  }, [loginWithGoogle, mode, onError, onSuccess, toast]);

  // ----- Apple -----------------------------------------------------------
  useEffect(() => {
    if (!APPLE_CLIENT_ID) return;
    let cancelled = false;
    loadScriptOnce('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js')
      .then(() => {
        if (cancelled || !window.AppleID) return;
        try {
          window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: 'name email',
            redirectURI: APPLE_REDIRECT_URI,
            usePopup: true,
          });
        } catch (e) {
          console.warn('Apple sign-in init failed:', e);
          setAppleAvailable(false);
        }
      })
      .catch((e) => {
        console.warn('Apple sign-in script failed to load:', e);
        setAppleAvailable(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleAppleClick = async () => {
    if (!window.AppleID?.auth) return;
    setAppleLoading(true);
    try {
      const res = await window.AppleID.auth.signIn();
      // res = { authorization: { id_token, code, state }, user?: { name, email } }
      const idToken = res?.authorization?.id_token;
      if (!idToken) throw new Error('Apple did not return an id_token');
      const result = await loginWithApple(idToken, res?.user || null);
      if (result?.requires2FA || result?.requires2FASetup) {
        onSuccess?.(result);
        return;
      }
      toast({
        title: mode === 'signup' ? 'Welcome to Ariv!' : 'Welcome back!',
        description: `Signed in as ${result.user.name}`,
      });
      onSuccess?.(result);
    } catch (err) {
      // Apple's SDK throws when the user cancels the popup — that's not
      // an error worth toasting about.
      const isUserCancel = err?.error === 'popup_closed_by_user' || err?.error === 'user_cancelled_authorize';
      if (!isUserCancel) {
        const detail = err.response?.data?.detail || err.message || 'Apple sign-in failed';
        toast({ title: 'Sign-in failed', description: detail, variant: 'destructive' });
        onError?.(err);
      }
    } finally {
      setAppleLoading(false);
    }
  };

  // Hide the block entirely if neither provider is configured -- avoids
  // showing dead buttons in dev / before env vars land in prod.
  if (!GOOGLE_CLIENT_ID && !APPLE_CLIENT_ID) {
    return null;
  }

  return (
    <div className="mt-6">
      <div className="relative flex items-center my-4">
        <div className="flex-grow border-t border-gray-200" />
        <span className="px-3 text-xs uppercase tracking-wide text-gray-400">or continue with</span>
        <div className="flex-grow border-t border-gray-200" />
      </div>

      <div className="flex flex-col items-center gap-3">
        {GOOGLE_CLIENT_ID && (
          <div ref={googleBtnRef} className="flex justify-center" />
        )}

        {APPLE_CLIENT_ID && (
          <button
            type="button"
            onClick={handleAppleClick}
            disabled={appleLoading || !appleAvailable}
            className="w-[320px] h-[44px] bg-black text-white rounded-md flex items-center justify-center gap-2 hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M13.06 11.4c-.25.57-.55 1.1-.9 1.6-.5.7-.9 1.2-1.2 1.5-.5.5-1.05.75-1.6.75-.4 0-.9-.1-1.4-.35-.55-.25-1.05-.35-1.5-.35-.5 0-1 .1-1.55.35-.55.25-1 .4-1.35.4-.55 0-1.1-.25-1.6-.75-.35-.35-.75-.85-1.25-1.55-.55-.75-1-1.6-1.35-2.6C.45 9.4.3 8.4.3 7.45c0-1.1.25-2.05.7-2.85.4-.65.9-1.15 1.55-1.55.65-.4 1.35-.6 2.1-.6.45 0 1 .15 1.65.4.6.25 1 .4 1.2.4.15 0 .55-.15 1.25-.45.65-.3 1.2-.4 1.65-.4 1.25.1 2.2.6 2.85 1.5-1.1.7-1.65 1.65-1.65 2.9 0 .95.35 1.75 1.05 2.4.3.3.65.5 1 .65-.1.25-.2.5-.3.7zM10.65 1.3c0 .8-.3 1.55-.85 2.25-.65.8-1.5 1.3-2.4 1.2-.05-.95.4-1.85.95-2.45.3-.3.7-.55 1.2-.75.5-.2.95-.3 1.4-.3.05.05.05.05.05.05z"/>
            </svg>
            <span className="font-medium text-sm">
              {appleLoading ? 'Signing in…' : (mode === 'signup' ? 'Sign up with Apple' : 'Sign in with Apple')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default SocialAuthButtons;
